import * as React from "react";
import { Polyline } from "@react-google-maps/api";
import type { LineDetails } from "../../types";
import { getNetworkColor } from "../../utils/networkColor";

type LinePolylinesProps = {
  line: LineDetails;
  activeLineId: string | null;
  polyRefs: React.MutableRefObject<Record<string, google.maps.Polyline[]>>;
  onClick?: (line: LineDetails) => void;
};

export const LinePolylines = React.memo(function LinePolylines({
  line,
  activeLineId,
  polyRefs,
  onClick,
}: LinePolylinesProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  const localPolysRef = React.useRef<google.maps.Polyline[]>([]);

  const isActive = String(line.id) === String(activeLineId);
  const isHighlighted = isActive || isHovered;

  const strokeColor = React.useMemo(() => {
    return getNetworkColor(String(line.id));
  }, [line.id]);

  const paths = React.useMemo(() => {
    if (line.paths && line.paths.length) return line.paths;

    return [line.stops.map((s) => ({ lat: s.lat, lng: s.lng }))];
  }, [line.paths, line.stops]);

  const options = React.useMemo<google.maps.PolylineOptions>(() => {
    return {
      strokeColor,
      strokeOpacity: isHighlighted ? 0.9 : 0.3,
      strokeWeight: isHighlighted ? 8 : 7,
      zIndex: isHovered ? 3000 : isActive ? 2000 : 8,
      clickable: true,
    };
  }, [strokeColor, isHighlighted, isHovered, isActive]);

  React.useEffect(() => {
    localPolysRef.current.forEach((poly) => {
      poly.setOptions(options);
    });
  }, [options]);

  React.useEffect(() => {
    return () => {
      document.body.style.cursor = "";
    };
  }, []);

  return (
    <>
      {paths.map((path, idx) => (
        <Polyline
          key={`${line.id}-path-${idx}`}
          path={path}
          options={options}
          onClick={() => onClick?.(line)}
          onMouseOver={() => {
            setIsHovered(true);
            document.body.style.cursor = "pointer";
          }}
          onMouseOut={() => {
            setIsHovered(false);
            document.body.style.cursor = "";
          }}
          onLoad={(poly) => {
            const id = String(line.id);

            localPolysRef.current.push(poly);
            (polyRefs.current[id] ||= []).push(poly);

            poly.setOptions(options);
          }}
          onUnmount={(poly) => {
            const id = String(line.id);

            localPolysRef.current = localPolysRef.current.filter(
              (p) => p !== poly
            );

            const arr = polyRefs.current[id] || [];
            const next = arr.filter((p) => p !== poly);

            if (next.length) {
              polyRefs.current[id] = next;
            } else {
              delete polyRefs.current[id];
            }

            poly.setMap(null);
          }}
        />
      ))}
    </>
  );
});
